/**
 * <Preferences /> — authenticated /preferences route.
 *
 * Same UX as the public ``/email-preferences?token=...`` page but for
 * users already logged into the app. Reuses the authenticated
 * ``/api/profile/topic-preferences`` endpoints — no JWT in URL needed.
 *
 * Why a separate route vs. just using the card embedded in /profile?
 *   1. Many users will land here from external "manage preferences"
 *      links inside emails, even while logged in. A predictable
 *      ``/preferences`` URL is friendlier to muscle memory than
 *      navigating into Profile → scroll down to the privacy card.
 *   2. The dedicated page can offer the "Unsubscribe from everything"
 *      bulk action without competing for space with the other
 *      profile-management surfaces.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { ShieldCheck, Loader2, BellOff, CheckCircle2, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LangContext";

export default function Preferences() {
  const { lang } = useLang();
  const isEs = lang === "es";
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["profile", "topic-preferences"],
    queryFn: async () => (await api.get("/profile/topic-preferences")).data,
    retry: false,
  });

  const update = useMutation({
    mutationFn: async ({ topic_id, subscription }) =>
      (await api.put("/profile/topic-preferences", { topic_id, subscription })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile", "topic-preferences"] });
      toast.success(isEs ? "Preferencia guardada" : "Preference saved");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || (isEs ? "Error" : "Error")),
  });

  const unsubAll = useMutation({
    mutationFn: async () =>
      (await api.post("/profile/topic-preferences/unsubscribe-all")).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["profile", "topic-preferences"] });
      const msg = isEs
        ? `Te dimos de baja de ${d.flipped} categoría${d.flipped === 1 ? "" : "s"}.`
        : `Unsubscribed from ${d.flipped} categor${d.flipped === 1 ? "y" : "ies"}.`;
      toast.success(msg);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || (isEs ? "Error" : "Error")),
  });

  const apiKeyMissing =
    error?.response?.data?.detail === "resend_api_key_not_configured" ||
    error?.response?.data?.detail === "resend_topics_unavailable";
  const topics = data?.topics || [];
  const allOptedOut = topics.length > 0 && topics.every((t) => t.subscription === "opt_out");
  const [pendingId, setPendingId] = useState(null);

  const flip = (topic_id, checked) => {
    setPendingId(topic_id);
    update.mutate(
      { topic_id, subscription: checked ? "opt_in" : "opt_out" },
      { onSettled: () => setPendingId(null) },
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto p-4 sm:p-8">
        <Link
          to="/profile"
          data-testid="prefs-back-link"
          className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft className="h-3 w-3" />
          {isEs ? "Volver al perfil" : "Back to profile"}
        </Link>

        <Card className="p-6 sm:p-8 rounded-2xl border-slate-200" data-testid="preferences-card">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="h-5 w-5 text-emerald-700" />
            <h1 className="font-display text-xl font-bold text-slate-900">
              {isEs ? "Preferencias de email" : "Email preferences"}
            </h1>
          </div>
          <p className="text-sm text-slate-600 mt-2 mb-5">
            {isEs
              ? "Elige qué tipos de email quieres recibir. Las transaccionales críticas (cambios de contraseña, recibos, alertas de seguridad) siempre se mandan."
              : "Choose which email types you want to receive. Critical transactional emails (password changes, receipts, security alerts) are always sent."}
          </p>

          {isLoading ? (
            <div
              className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center"
              data-testid="prefs-loading"
            >
              <Loader2 className="h-4 w-4 animate-spin" />
              {isEs ? "Cargando preferencias…" : "Loading preferences…"}
            </div>
          ) : apiKeyMissing ? (
            <div
              className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900"
              data-testid="prefs-resend-unavailable"
            >
              <BellOff className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                {isEs
                  ? "Las preferencias por email aún no están disponibles. Pídele al admin que configure la API key del proveedor de email."
                  : "Email preferences aren't available yet. Ask your admin to configure the email provider API key."}
              </span>
            </div>
          ) : topics.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-400" data-testid="prefs-empty">
              {isEs
                ? "Sin categorías configuradas todavía."
                : "No categories configured yet."}
            </div>
          ) : (
            <div className="divide-y divide-slate-100" data-testid="prefs-topic-list">
              {topics.map((t) => (
                <PrefRow
                  key={t.id}
                  label={t.name}
                  hint={t.description}
                  checked={t.subscription !== "opt_out"}
                  disabled={pendingId === t.id || unsubAll.isPending}
                  onChange={(v) => flip(t.id, v)}
                  testid={`prefs-topic-${t.id}`}
                />
              ))}
            </div>
          )}

          {topics.length > 0 && !allOptedOut && (
            <div className="border-t border-slate-200 mt-5 pt-5 flex items-center justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => unsubAll.mutate()}
                disabled={unsubAll.isPending || update.isPending}
                className="text-rose-700 border-rose-200 hover:bg-rose-50 gap-1"
                data-testid="prefs-unsubscribe-all"
              >
                {unsubAll.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <BellOff className="h-3 w-3" />
                )}
                {isEs ? "Desuscribirme de todo" : "Unsubscribe from everything"}
              </Button>
            </div>
          )}

          {allOptedOut && (
            <div
              className="mt-5 p-3 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-600 flex items-center gap-2"
              data-testid="prefs-all-opted-out"
            >
              <CheckCircle2 className="h-4 w-4 text-slate-500" />
              {isEs
                ? "Estás dado de baja de todas las categorías. Vuelve a habilitar las que quieras arriba."
                : "You're opted out of everything. Re-enable any topic above when you want it back."}
            </div>
          )}
        </Card>

        <p className="text-[10px] text-slate-400 mt-4 text-center">
          {isEs
            ? "Tus preferencias se guardan en Lumfi (única fuente de verdad)."
            : "Your preferences are stored in Lumfi (single source of truth)."}
        </p>
      </div>
    </div>
  );
}

function PrefRow({ label, hint, checked, onChange, testid, disabled }) {
  return (
    <label className="flex items-start justify-between gap-3 py-3 cursor-pointer group">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        {hint && <div className="text-xs text-slate-500 mt-0.5">{hint}</div>}
      </div>
      <button /* design-system: ALLOW legacy-todo */
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        disabled={disabled}
        data-testid={testid}
        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 transition-colors ${
          checked ? "bg-emerald-600 border-emerald-700" : "bg-slate-200 border-slate-300"
        } disabled:opacity-50`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${
            checked ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </button>
    </label>
  );
}
